import { UserManager, WebStorageStateStore } from 'oidc-client';
import { stripStayParam, initialStayArgs } from '@/utils/stayParam';
// Enterprise-SSO slug persistence (Epic CORE-6317) — lifetime rules live in that module.
import { normalizeSsoSlug, getSsoSlug, setSsoSlug, clearSsoSlug, ssoAcrArgs } from '@/utils/ssoSlug';

// eslint-disable-next-line import/no-dynamic-require
const { config } = require(`../config/config.${process.env.NODE_ENV}`);

// oidc-client rejects querySessionStatus with an ErrorResponse (has `.error`, e.g.
// login_required) when the IS answered "no session", and with a plain Error on
// timeouts / network failures, which prove nothing about the session.
function isOidcErrorResponse(error) {
    return Boolean(error) && typeof error.error === 'string';
}

export default class AuthService {
    constructor() {
        const defaultSettings = {
            client_id: 'locate2u.appportal',
            response_type: 'id_token token',
            scope: 'openid profile locate2u.appportal',
            userStore: new WebStorageStateStore({
                store: window.localStorage
            }),
            automaticSilentRenew: true,
            accessTokenExpiringNotificationTime: 10
            // acr_values: 'pti:995e7cd9-e84f-41f0-b4fc-3917a7f239e3' // this is used for white labeling login page for the identity server
        };

        const settings = Object.assign({}, defaultSettings, config);

        this.userManager = new UserManager(settings);
        this.isLoggingOut = false;

        // When the built-in SessionMonitor detects the IS session has ended
        // (e.g. cross-portal logout), clear local state and redirect to login
        const self = this;
        this.userManager.events.addUserSignedOut(async function handleUserSignedOut() {
            if (self.isLoggingOut) return;
            const user = await self.userManager.getUser();
            if (!user) return;
            // check_session reported a change and the follow-up probe failed: treated as an
            // explicit logout (CORE-7425), so the branded login is forgotten.
            self.endLocalSessionAndReauth({ keepSsoSlug: false });
        });
    }

    async getUser() {
        const response = await this.userManager.getUser();
        return response;
    }

    async loginCallback() {
        const response = await this.userManager.signinRedirectCallback();
        return response;
    }

    async login() {
        // CORE-6841: keep the stay marker OUT of `state` — Callback.vue compares the restored state
        // against '/' to decide whether to route a new user into onboarding — but forward it as an
        // authorize query param so the Identity Server knows this was a deliberate return to the
        // App Portal and leaves an opted-in team's user here.
        const redirect = stripStayParam(`${window.location.pathname}${window.location.search}`);
        // Replay the persisted enterprise-SSO slug (if any) so a re-auth from the auth
        // guard on an expired/no session routes an SSO user to their branded
        // /{slug}/login. Explicit logout clears the slug first, so this sends nothing there.
        const response = await this.userManager.signinRedirect(
            Object.assign({ state: redirect }, ssoAcrArgs(), initialStayArgs())
        );
        return response;
    }

    async register() {
        const response = await this.userManager.signinRedirect({
            extraQueryParams: {
                page: 'register'
            }
        });
        return response;
    }

    // Branded enterprise SSO entry (Epic CORE-6317 F9). Tag the authorize request with the
    // customer slug (acr_values=sso:<slug>) so the Identity Server routes the user to
    // /{slug}/login → their Entra sign-in. NOTE: uses the custom `sso:` prefix, not `tenant:` —
    // IdentityServer reserves and strips `tenant:`/`idp:` out of acr_values. Land on the app root
    // after sign-in (not back on /{slug}/login, which would re-trigger the redirect).
    async loginWithSso(slug) {
        // Normalise up front so the persisted slug and the acr_values we send are the
        // same value — previously acr_values used the raw param, so a slug the IS could
        // never resolve (reserved word, underscore, …) still fired an authorize request.
        const normalized = normalizeSsoSlug(slug);
        if (!normalized) {
            throw new Error(`Invalid enterprise-SSO slug: ${slug}`);
        }
        // Persist the slug so the router guard's re-auth on token expiry replays it via
        // ssoAcrArgs() and returns the user to this branded page. Explicit logout clears it.
        // Callers MUST only reach here for an unauthenticated user — see SsoLogin.vue.
        setSsoSlug(normalized);
        const response = await this.userManager.signinRedirect({
            state: '/',
            acr_values: `sso:${normalized}`
        });
        return response;
    }

    async logout() {
        this.isLoggingOut = true;

        // Explicit logout forgets the enterprise-SSO slug (CORE-7425) so the post-logout
        // landing on /login starts the generic sign-in, not the customer's branded page.
        clearSsoSlug();

        // signoutRedirect properly terminates the IS session via
        // /connect/endsession (top-level navigation sends cookies).
        // This ensures cross-portal logout propagates correctly.
        // The _isLoggingOut flag prevents addUserSignedOut and
        // checkSessionValidity from racing with this redirect.
        await this.userManager.signoutRedirect();
    }

    async isLoggedIn() {
        const user = await this.getUser();

        return user !== null && !user.expired;
    }

    /**
     * Check if the IS session is still valid.
     * Called on tab visibility change to detect cross-portal logout.
     */
    async checkSessionValidity() {
        if (this.isLoggingOut) return;

        const user = await this.userManager.getUser();
        if (!user || user.expired) {
            return;
        }

        try {
            // Use querySessionStatus instead of signinSilent — signinSilent
            // can succeed via refresh token even when the IS session is gone.
            // querySessionStatus checks the IS session directly via iframe.
            await this.userManager.querySessionStatus();
        } catch (error) {
            // Only an IS "no session" answer (cross-portal logout or IS-session expiry) counts
            // as an explicit logout and forgets the branded login (CORE-7425). A timeout or
            // network failure proves nothing, so the slug survives that re-auth.
            await this.endLocalSessionAndReauth({ keepSsoSlug: !isOidcErrorResponse(error) });
        }
    }

    // Wipes local auth state and starts a fresh sign-in. keepSsoSlug carries the
    // enterprise-SSO slug through the wipe so the sign-in stays branded; otherwise the
    // slug goes with everything else and the sign-in is generic.
    async endLocalSessionAndReauth({ keepSsoSlug }) {
        const slug = keepSsoSlug ? getSsoSlug() : null;
        window.localStorage.clear();
        window.sessionStorage.clear();
        if (slug) {
            setSsoSlug(slug);
        }
        await this.userManager.signinRedirect(ssoAcrArgs());
    }

    /**
     * Set up session monitoring for cross-portal logout detection.
     * Checks IS session on tab visibility change AND on initial load.
     */
    setupSessionMonitoring() {
        const self = this;

        // Check on tab visibility change (switching back to this tab)
        document.addEventListener('visibilitychange', function handleVisibilityChange() {
            if (document.visibilityState === 'visible') {
                self.checkSessionValidity();
            }
        });

        // Check on initial load — catches cross-portal logout
        // that happened while this tab was closed or freshly opened
        this.checkSessionValidity();

        // Background check every 5 minutes — catches cross-portal logout
        // when user stays on the same tab without switching
        setInterval(function checkSession() {
            self.checkSessionValidity();
        }, 5 * 60 * 1000);
    }
}
